import { z } from 'zod';

// src/version.ts
var API_VERSION = "v1";
var CONTRACT_VERSION = "1.4.0";
var SCREENSHOT_PATH = `/${API_VERSION}/screenshot`;

// src/screenshot/limits.ts
var MAX_SELECTOR_LENGTH = 200;
var SCREENSHOT_LIMITS = {
  /** Bounded so a hostile query string cannot make parsing or logging expensive. */
  urlMaxLength: 2048,
  viewportWidth: { min: 200, max: 3840, default: 1280 },
  viewportHeight: { min: 200, max: 4320, default: 720 },
  deviceScaleFactor: { min: 0.5, max: 3, default: 1 },
  /** Applies to `jpeg` and `webp` only; `png` is lossless and rejects an explicit quality. */
  quality: { min: 1, max: 100, lossyDefault: 80 },
  delayMs: { min: 0, max: 1e4, default: 0 },
  timeoutMs: { min: 1e3, max: 3e4, default: 15e3 },
  cacheTtlSeconds: { min: 0, max: 86400, default: 0 },
  /**
   * Customer-supplied CSS and JavaScript, injected into the page before the capture.
   *
   * Four kilobytes each, which is a tweak rather than a bundle. Someone who needs more than this is
   * describing a page they should be hosting themselves and capturing.
   *
   * Enforced in characters, whichever method carries them, which is not the same as bytes on the
   * wire. As a `GET`, a maximal request of typical source encodes to about 15 KB of URL and fits
   * comfortably. Text outside ASCII does not: a character such as `€` or `日` is three bytes of
   * UTF-8 and nine characters once percent-encoded, so a request at every limit written that way
   * passes 90 KB. Cloudflare's edge refuses a request target over 65,534 bytes with a bare
   * `414` -- no JSON, no request id, the gateway never runs -- as measured on 2026-09-24. A `POST`
   * body has no such ceiling (see {@link SCREENSHOT_BODY_MAX_BYTES}), which is one of the reasons it
   * is accepted.
   */
  customCss: { maxLength: 4096 },
  customJs: { maxLength: 4096 },
  hideSelectors: {
    /** Maximum number of distinct selectors after deduplication. */
    maxCount: 20,
    /** Maximum length of a single selector. */
    maxSelectorLength: MAX_SELECTOR_LENGTH,
    /** Maximum combined length of all selectors, which bounds renderer work. */
    maxTotalLength: 1e3
  },
  /** The one element to capture. */
  selector: { maxLength: MAX_SELECTOR_LENGTH }
};
var SCREENSHOT_BODY_MAX_BYTES = 6 * (SCREENSHOT_LIMITS.urlMaxLength + SCREENSHOT_LIMITS.customCss.maxLength + SCREENSHOT_LIMITS.customJs.maxLength + SCREENSHOT_LIMITS.hideSelectors.maxTotalLength + SCREENSHOT_LIMITS.selector.maxLength) + 4096;
var SCREENSHOT_FORMATS = ["png", "jpeg", "webp"];
var LOSSY_FORMATS = ["jpeg", "webp"];
function isLossyFormat(format) {
  return LOSSY_FORMATS.includes(format);
}
var FORMAT_CONTENT_TYPE = {
  png: "image/png",
  jpeg: "image/jpeg",
  webp: "image/webp"
};
var WAIT_UNTIL_VALUES = [
  "auto",
  "load",
  "domcontentloaded",
  "networkidle0",
  "networkidle2"
];
var DEFAULT_WAIT_UNTIL = "auto";
var AUTO_WAIT = {
  quietMs: 500,
  maxMs: 3e3
};
var ALLOWED_TARGET_PROTOCOLS = ["http:", "https:"];

// src/demo.ts
var DEMO_PATH = `/${API_VERSION}/demo`;
var DEMO_WORKSPACE_ID = "019a0000-0000-7000-8000-00000000de30";
var DEMO_VIEWPORTS = {
  desktop: { width: 1280, height: 800 },
  mobile: { width: 390, height: 844 }
};
var DEMO_VIEWPORT_NAMES = ["desktop", "mobile"];
var DEMO_RENDER = {
  format: "webp",
  quality: SCREENSHOT_LIMITS.quality.lossyDefault,
  timeoutMs: 1e4,
  cacheTtlSeconds: 86400
};
var DEMO_LIMITS = {
  visitorRendersPerHour: 5,
  visitorRendersPerDay: 20,
  visitorRequestsPerHour: 30,
  rendersPerDay: 300
};
var DEMO_BODY_MAX_BYTES = 4096;
var demoRequestSchema = z.strictObject({
  url: z.string().trim().min(1).max(SCREENSHOT_LIMITS.urlMaxLength),
  viewport: z.enum(DEMO_VIEWPORT_NAMES).default("desktop"),
  dark_mode: z.boolean().default(false),
  /** Cloudflare Turnstile response token. Single-use and verified server-side before anything else. */
  turnstile_token: z.string().min(1).max(2048)
});
function demoScreenshotQuery(request) {
  const viewport = DEMO_VIEWPORTS[request.viewport];
  return new URLSearchParams({
    url: request.url,
    format: DEMO_RENDER.format,
    quality: String(DEMO_RENDER.quality),
    viewport_width: String(viewport.width),
    viewport_height: String(viewport.height),
    dark_mode: String(request.dark_mode),
    // Always on, and not a visitor control. The demo captures someone's own site to show them what
    // the API does; a consent dialog covering the result is the worst possible first impression, and
    // the one thing they cannot have anticipated.
    block_cookie_banners: "true",
    timeout_ms: String(DEMO_RENDER.timeoutMs),
    cache_ttl: String(DEMO_RENDER.cacheTtlSeconds)
  });
}
var DEMO_ERROR_CODES = [
  "demo_unavailable",
  "demo_verification_failed",
  "demo_limit_reached"
];
var DEMO_ERROR_HTTP_STATUS = {
  demo_unavailable: 503,
  demo_verification_failed: 403,
  demo_limit_reached: 429
};
var DEMO_ERROR_MESSAGES = {
  demo_unavailable: "The demo is paused. Create a free account to keep going.",
  // Does not say which check failed. A script working out how to pass it learns nothing here.
  demo_verification_failed: "This request could not be verified. Reload the page and try again.",
  demo_limit_reached: "The demo limit has been reached. Create a free account to keep going."
};
var demoErrorBodySchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    requestId: z.string().min(1)
  })
});
function demoErrorBody(code, message, requestId) {
  return { error: { code, message, requestId } };
}
var ERROR_CODES = [
  "invalid_request",
  "invalid_api_key",
  "revoked_api_key",
  "invalid_signature",
  "rate_limit_exceeded",
  "concurrency_limit_exceeded",
  "monthly_limit_exceeded",
  "target_not_allowed",
  "navigation_failed",
  "render_timeout",
  "render_failed",
  "script_failed",
  "selector_not_found",
  "service_unavailable"
];
var errorCodeSchema = z.enum(ERROR_CODES);
var REQUEST_STATUSES = ["succeeded", "failed", "rejected", "throttled"];
var requestStatusSchema = z.enum(REQUEST_STATUSES);
var ERROR_HTTP_STATUS = {
  invalid_request: 400,
  invalid_api_key: 401,
  revoked_api_key: 401,
  invalid_signature: 401,
  rate_limit_exceeded: 429,
  concurrency_limit_exceeded: 429,
  monthly_limit_exceeded: 429,
  target_not_allowed: 400,
  navigation_failed: 502,
  render_timeout: 504,
  render_failed: 502,
  // 400, not 502: the page loaded and the browser worked. What failed is the caller's own script,
  // and the thing that changes the outcome is their code, not a retry.
  script_failed: 400,
  // 400 for the same reason: the page loaded, and what changes the outcome is the caller's
  // selector -- or their `wait_until`, for an element the page adds late.
  selector_not_found: 400,
  service_unavailable: 503
};
var ERROR_STATUS = {
  invalid_request: "rejected",
  invalid_api_key: "rejected",
  revoked_api_key: "rejected",
  invalid_signature: "rejected",
  rate_limit_exceeded: "throttled",
  concurrency_limit_exceeded: "throttled",
  monthly_limit_exceeded: "rejected",
  target_not_allowed: "rejected",
  navigation_failed: "failed",
  render_timeout: "failed",
  render_failed: "failed",
  // `failed`, not `rejected`, although the status is 400: a browser ran, so the credit is spent
  // and the request log must show it as an attempt rather than as a request turned away.
  script_failed: "failed",
  selector_not_found: "failed",
  service_unavailable: "failed"
};
var RETRYABLE_ERROR_CODES = [
  "rate_limit_exceeded",
  "concurrency_limit_exceeded",
  "service_unavailable"
];
function isPreBrowserErrorCode(code) {
  return code !== "navigation_failed" && code !== "render_timeout" && code !== "render_failed" && code !== "script_failed" && code !== "selector_not_found";
}
var errorBodySchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string().min(1),
    requestId: z.string().min(1)
  })
});
function errorBody(code, message, requestId) {
  return { error: { code, message, requestId } };
}
function describeValidationIssues(issues) {
  const described = issues.map((issue) => {
    const path = issue.path.map(String).join(".");
    return path === "" ? issue.message : `${path}: ${issue.message}`;
  });
  return [...new Set(described)].sort().join("; ");
}
var ERROR_MESSAGES = {
  invalid_request: "The request could not be processed because one or more parameters are invalid.",
  invalid_api_key: "The provided API key is not valid.",
  revoked_api_key: "The provided API key has been revoked.",
  // Deliberately does not distinguish an absent signature from a wrong one. Which half failed is
  // exactly what an attacker probing the endpoint wants to know.
  invalid_signature: "The request signature is missing or does not match.",
  rate_limit_exceeded: "The request rate limit for this API key has been exceeded.",
  concurrency_limit_exceeded: "The workspace is already running the maximum number of concurrent renders.",
  monthly_limit_exceeded: "The workspace has used all credits for the current period.",
  target_not_allowed: "The requested target address is not allowed.",
  navigation_failed: "The target page could not be loaded.",
  render_timeout: "The target page did not finish rendering before the timeout.",
  render_failed: "The screenshot could not be produced.",
  script_failed: "The supplied custom_js did not run to completion.",
  selector_not_found: "The selector matched no visible element on the page.",
  service_unavailable: "The screenshot service is temporarily unavailable."
};
var { viewportWidth, viewportHeight, deviceScaleFactor, quality, delayMs, timeoutMs } = SCREENSHOT_LIMITS;
var { cacheTtlSeconds, hideSelectors } = SCREENSHOT_LIMITS;
var SELECTOR_PATTERN = /^[A-Za-z0-9_\-.#*>+~,:()[\]="'^$|/\s]+$/;
var SELECTOR_SUBSTANCE_PATTERN = /[A-Za-z0-9_\-*#.[\]:]/;
function isValidSelector(value) {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.length > hideSelectors.maxSelectorLength) return false;
  if (!SELECTOR_PATTERN.test(trimmed)) return false;
  return SELECTOR_SUBSTANCE_PATTERN.test(trimmed);
}
function normalizeSelectors(values) {
  const seen = /* @__PURE__ */ new Set();
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed.length > 0) seen.add(trimmed);
  }
  return [...seen].sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
}
var screenshotFieldSchemas = {
  /**
   * Only the shape of the URL is checked here. Whether the *target* is permitted -- scheme,
   * embedded credentials, and resolved address -- is decided by `validateTargetUrl` and by the
   * renderer's DNS checks, because those rejections are `target_not_allowed` rather than
   * `invalid_request`.
   */
  url: z.url().max(SCREENSHOT_LIMITS.urlMaxLength).describe("Absolute HTTP or HTTPS URL of the page to capture."),
  format: z.enum(SCREENSHOT_FORMATS).default("png").describe("Output image format."),
  viewport_width: z.int().min(viewportWidth.min).max(viewportWidth.max).default(viewportWidth.default).describe("Viewport width in CSS pixels."),
  viewport_height: z.int().min(viewportHeight.min).max(viewportHeight.max).default(viewportHeight.default).describe(
    "Viewport height in CSS pixels. Ignored for the captured height when full_page is true or a selector is given."
  ),
  device_scale_factor: z.number().min(deviceScaleFactor.min).max(deviceScaleFactor.max).default(deviceScaleFactor.default).describe("Device pixel ratio to emulate."),
  full_page: z.boolean().default(false).describe("Capture the full scrollable page instead of only the viewport."),
  /**
   * Capture one element instead of the viewport.
   *
   * Plain CSS, matched with `document.querySelector`, so the first match in document order is the
   * one captured. The renderer waits for it to appear, inside the same budget as `custom_js`, and
   * captures its whole box even when it is taller than the viewport -- which is why it cannot be
   * combined with `full_page`: the two answer the same question, how much to capture.
   *
   * A selector that matches nothing, or only an element with no visible size, fails with
   * `selector_not_found`. It fails after the browser has run, so it is charged, as `script_failed`
   * is (ADR 0026).
   */
  selector: z.string().max(SCREENSHOT_LIMITS.selector.maxLength).refine(isValidSelector).describe(
    "CSS selector of one element to capture instead of the viewport. The first match is captured at its full size, even when it is taller than the viewport. Cannot be combined with full_page."
  ),
  quality: z.int().min(quality.min).max(quality.max).describe(
    `Encoder quality for jpeg and webp. Defaults to ${String(quality.lossyDefault)} for those formats and must be omitted for png.`
  ),
  wait_until: z.enum(WAIT_UNTIL_VALUES).default(DEFAULT_WAIT_UNTIL).describe(
    `When to capture. auto waits for the load event, then until no request has been in flight for ${String(AUTO_WAIT.quietMs)} ms, for at most ${String(AUTO_WAIT.maxMs)} ms more; at that limit it captures the page as it is. The others are the browser's navigation lifecycle events, and fail with render_timeout if the event does not come within timeout_ms.`
  ),
  delay_ms: z.int().min(delayMs.min).max(delayMs.max).default(delayMs.default).describe("Additional wait after the navigation condition is met. Also bounded by the plan."),
  timeout_ms: z.int().min(timeoutMs.min).max(timeoutMs.max).default(timeoutMs.default).describe("Maximum navigation time. Also bounded by the plan."),
  dark_mode: z.boolean().default(false).describe("Emulate prefers-color-scheme: dark before navigation."),
  hide_selectors: z.string().max(hideSelectors.maxSelectorLength).refine(isValidSelector).describe("CSS selector whose matching elements are hidden after navigation. Repeatable."),
  /**
   * Hide consent dialogs, and undo the scroll lock they leave behind.
   *
   * The counterpart to `hide_selectors` for the one case every caller hits: the selectors are ours,
   * maintained against the consent platforms rather than supplied per site. It hides -- no consent
   * button is pressed on anyone's behalf -- and it restores `overflow` on the document, because a
   * platform that locks scrolling silently truncates a `full_page` capture at the fold.
   *
   * What it cannot do is answer the dialog. A site that withholds its content until a choice is made
   * still withholds it; the capture is simply no longer covered by a banner.
   */
  block_cookie_banners: z.boolean().default(false).describe(
    "Hide known consent dialogs and restore page scrolling before capturing. Available on every plan."
  ),
  /**
   * CSS appended to the page after it has loaded, as the last stylesheet.
   *
   * Injected through the browser's own devtools protocol rather than as a `<style>` element, so a
   * target page's Content-Security-Policy cannot silently drop it. Anything a stylesheet can do to
   * a page it can do here, and nothing more: CSS cannot read the document or make a request that
   * the renderer's request guard does not already see.
   *
   * Rules the browser cannot parse are dropped, as they are in any stylesheet, and `@import` is
   * ignored. Neither fails the request: being stricter about the caller's CSS than about the
   * page's own would reject stylesheets that work perfectly well everywhere else.
   */
  custom_css: z.string().max(SCREENSHOT_LIMITS.customCss.maxLength).describe("CSS injected into the page after it loads, as the last stylesheet."),
  /**
   * JavaScript evaluated in the page once it is ready, before the capture.
   *
   * Evaluated after the navigation condition and any `delay_ms`, so it sees the page a visitor
   * would. It runs in the page's own context, which means it can do what the page can do -- and no
   * more: every request it makes passes the same address guard as the page's own, so it cannot
   * reach a private address or the instance metadata service.
   *
   * A script that throws, or that does not settle inside the remaining render budget, fails the
   * request with `script_failed`. Returning an image that the customer's own code did not finish
   * shaping would be a screenshot of something they did not ask for.
   */
  custom_js: z.string().max(SCREENSHOT_LIMITS.customJs.maxLength).describe("JavaScript evaluated in the page before the capture. Paid plans only."),
  cache_ttl: z.int().min(cacheTtlSeconds.min).max(cacheTtlSeconds.max).default(cacheTtlSeconds.default).describe("Seconds an identical request may be served from cache. 0 bypasses the cache.")
};
var normalizedScreenshotOptionsObject = z.object({
  url: screenshotFieldSchemas.url,
  format: screenshotFieldSchemas.format,
  viewport_width: screenshotFieldSchemas.viewport_width,
  viewport_height: screenshotFieldSchemas.viewport_height,
  device_scale_factor: screenshotFieldSchemas.device_scale_factor,
  full_page: screenshotFieldSchemas.full_page,
  // Nullable for the reason `quality` is, and defaulted as well: the renderer is deployed before
  // the gateway (docs/deployment.md), and in that window a gateway that predates `selector` sends
  // no key at all. Zod refuses a missing key on a field that is only nullable, so without the
  // default every render in the window would fail as `invalid_request`.
  selector: screenshotFieldSchemas.selector.nullable().default(null),
  quality: screenshotFieldSchemas.quality.nullable(),
  wait_until: screenshotFieldSchemas.wait_until,
  delay_ms: screenshotFieldSchemas.delay_ms,
  timeout_ms: screenshotFieldSchemas.timeout_ms,
  dark_mode: screenshotFieldSchemas.dark_mode,
  hide_selectors: z.array(screenshotFieldSchemas.hide_selectors).max(hideSelectors.maxCount),
  block_cookie_banners: screenshotFieldSchemas.block_cookie_banners,
  // Nullable rather than optional, for the same reason `quality` is: the cache-key material walks
  // a fixed set of keys, and an absent key and a null one must not hash differently.
  custom_css: screenshotFieldSchemas.custom_css.nullable(),
  custom_js: screenshotFieldSchemas.custom_js.nullable(),
  cache_ttl: screenshotFieldSchemas.cache_ttl
});
var recordedScreenshotOptionsSchema = normalizedScreenshotOptionsObject.omit({ url: true, custom_css: true, custom_js: true }).extend({
  custom_css_used: z.boolean(),
  custom_js_used: z.boolean()
});
var normalizedScreenshotOptionsSchema = normalizedScreenshotOptionsObject.superRefine(
  (options, ctx) => {
    if (options.format === "png" && options.quality !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["quality"],
        message: "Only supported for the jpeg and webp formats."
      });
    }
    if (isLossyFormat(options.format) && options.quality === null) {
      ctx.addIssue({
        code: "custom",
        path: ["quality"],
        message: "Required for the jpeg and webp formats."
      });
    }
    if (options.selector !== null && options.full_page) {
      ctx.addIssue({
        code: "custom",
        path: ["selector"],
        message: "Cannot be combined with full_page."
      });
    }
  }
);
var CONTENT_AFFECTING_OPTIONS = [
  "url",
  "format",
  "viewport_width",
  "viewport_height",
  "device_scale_factor",
  "full_page",
  // Added in 1.4.0 like every option before it: every key gained `selector=null`, so each entry
  // cached before the gateway deploy was missed once and re-rendered (ADR 0026).
  "selector",
  "quality",
  "wait_until",
  "delay_ms",
  "dark_mode",
  "hide_selectors",
  "block_cookie_banners",
  // Both change the pixels, so both are part of the cache identity. Leaving either out would serve
  // a customer the capture their *previous* stylesheet produced, which is the kind of cache bug
  // that is only ever found by the person it confuses.
  "custom_css",
  "custom_js"
];
var SECRET_KEY_PREFIX = "sk_";
var PUBLISHABLE_KEY_PREFIX = "pk_";
var KEY_KINDS = ["secret", "publishable"];
var keyKindSchema = z.enum(KEY_KINDS);
function keyPrefixFor(kind) {
  return kind === "publishable" ? PUBLISHABLE_KEY_PREFIX : SECRET_KEY_PREFIX;
}

// src/screenshot/signing.ts
var SIGNATURE_PARAMETER = "signature";
var SIGNED_KEY_PARAMETER = "key";
function toHex(buffer) {
  return Array.from(new Uint8Array(buffer)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
async function hmac(secret, payload) {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return toHex(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(payload)));
}
function timingSafeEqualSignature(a, b) {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let index = 0; index < a.length; index += 1) {
    difference |= a.charCodeAt(index) ^ b.charCodeAt(index);
  }
  return difference === 0;
}
function buildSignedQueryPayload(rawQuery) {
  const query = rawQuery.startsWith("?") ? rawQuery.slice(1) : rawQuery;
  const signed = query.split("&").filter((pair) => pair !== "" && !pair.startsWith(`${SIGNATURE_PARAMETER}=`));
  return signed.join("&");
}
function readSignature(rawQuery) {
  const query = rawQuery.startsWith("?") ? rawQuery.slice(1) : rawQuery;
  for (const pair of query.split("&")) {
    if (pair.startsWith(`${SIGNATURE_PARAMETER}=`)) {
      const value = pair.slice(SIGNATURE_PARAMETER.length + 1);
      return value === "" ? null : value;
    }
  }
  return null;
}
async function signScreenshotQuery(input) {
  return hmac(input.secret, buildSignedQueryPayload(input.rawQuery));
}
async function verifyScreenshotQuery(input) {
  const presented = readSignature(input.rawQuery);
  if (presented === null) return { ok: false, reason: "missing_signature" };
  const expected = await hmac(input.secret, buildSignedQueryPayload(input.rawQuery));
  return timingSafeEqualSignature(presented, expected) ? { ok: true } : { ok: false, reason: "bad_signature" };
}

// src/screenshot/parse.ts
var SCREENSHOT_PARAMETER_NAMES = [
  "url",
  "format",
  "viewport_width",
  "viewport_height",
  "device_scale_factor",
  "full_page",
  "selector",
  "quality",
  "wait_until",
  "delay_ms",
  "timeout_ms",
  "dark_mode",
  "hide_selectors",
  "block_cookie_banners",
  "custom_css",
  "custom_js",
  "cache_ttl"
];
var REPEATABLE_PARAMETERS = /* @__PURE__ */ new Set(["hide_selectors"]);
var TRUE_VALUES = /* @__PURE__ */ new Set(["true", "1"]);
var FALSE_VALUES = /* @__PURE__ */ new Set(["false", "0"]);
function parseBoolean(raw, parameter, issues) {
  const value = raw.trim().toLowerCase();
  if (TRUE_VALUES.has(value)) return true;
  if (FALSE_VALUES.has(value)) return false;
  issues.push({ parameter, message: "Must be one of: true, false, 1, 0." });
  return null;
}
function parseInteger(raw, parameter, bounds, issues) {
  const value = raw.trim();
  if (!/^\d+$/.test(value)) {
    issues.push({ parameter, message: "Must be a non-negative integer." });
    return null;
  }
  const parsed = Number(value);
  if (parsed < bounds.min || parsed > bounds.max) {
    issues.push({
      parameter,
      message: `Must be between ${String(bounds.min)} and ${String(bounds.max)}.`
    });
    return null;
  }
  return parsed;
}
function parseDecimal(raw, parameter, bounds, issues) {
  const value = raw.trim();
  if (!/^\d+(\.\d+)?$/.test(value)) {
    issues.push({ parameter, message: "Must be a non-negative number." });
    return null;
  }
  const parsed = Number(value);
  if (parsed < bounds.min || parsed > bounds.max) {
    issues.push({
      parameter,
      message: `Must be between ${String(bounds.min)} and ${String(bounds.max)}.`
    });
    return null;
  }
  return parsed;
}
function parseEnum(raw, parameter, allowed, issues) {
  const value = raw.trim().toLowerCase();
  if (allowed.includes(value)) return value;
  issues.push({ parameter, message: `Must be one of: ${allowed.join(", ")}.` });
  return null;
}
function parseSource(raw, parameter, maxLength, issues) {
  if (raw.trim() === "") {
    issues.push({ parameter, message: "Must not be empty." });
    return null;
  }
  if (raw.length > maxLength) {
    issues.push({
      parameter,
      message: `Must not exceed ${String(maxLength)} characters.`
    });
    return null;
  }
  return raw;
}
function parseHideSelectors(values, issues) {
  if (values.some((value) => value.trim() === "")) {
    issues.push({ parameter: "hide_selectors", message: "Must not be empty." });
    return [];
  }
  const normalized = normalizeSelectors(values);
  if (normalized.length > SCREENSHOT_LIMITS.hideSelectors.maxCount) {
    issues.push({
      parameter: "hide_selectors",
      message: `At most ${String(SCREENSHOT_LIMITS.hideSelectors.maxCount)} distinct selectors are allowed.`
    });
    return [];
  }
  const invalid = normalized.filter((selector) => !isValidSelector(selector));
  if (invalid.length > 0) {
    issues.push({
      parameter: "hide_selectors",
      message: `Contains an unsupported CSS selector. Each selector must be 1 to ${String(SCREENSHOT_LIMITS.hideSelectors.maxSelectorLength)} characters and use only standard selector syntax.`
    });
    return [];
  }
  const totalLength = normalized.reduce((sum, selector) => sum + selector.length, 0);
  if (totalLength > SCREENSHOT_LIMITS.hideSelectors.maxTotalLength) {
    issues.push({
      parameter: "hide_selectors",
      message: `Combined selector length must not exceed ${String(SCREENSHOT_LIMITS.hideSelectors.maxTotalLength)} characters.`
    });
    return [];
  }
  return normalized;
}
function parseSelector(raw, issues) {
  const selector = raw.trim();
  if (selector === "") {
    issues.push({ parameter: "selector", message: "Must not be empty." });
    return null;
  }
  if (!isValidSelector(selector)) {
    issues.push({
      parameter: "selector",
      message: `Must be 1 to ${String(SCREENSHOT_LIMITS.selector.maxLength)} characters and use only standard selector syntax.`
    });
    return null;
  }
  return selector;
}
var REPORTABLE_PARAMETER_NAME = /^[A-Za-z0-9_-]{1,32}$/;
function reportableName(name) {
  return REPORTABLE_PARAMETER_NAME.test(name) ? name : "unknown parameter";
}
function parseScreenshotQuery(params) {
  const issues = [];
  const known = new Set(SCREENSHOT_PARAMETER_NAMES);
  for (const name of new Set(params.keys())) {
    if (!known.has(name)) {
      issues.push({ parameter: reportableName(name), message: "Unknown parameter." });
      continue;
    }
    if (!REPEATABLE_PARAMETERS.has(name) && params.getAll(name).length > 1) {
      issues.push({ parameter: name, message: "May only be supplied once." });
    }
  }
  const rawUrl = params.get("url");
  if (rawUrl === null || rawUrl.trim() === "") {
    issues.push({ parameter: "url", message: "Required." });
  } else if (rawUrl.length > SCREENSHOT_LIMITS.urlMaxLength) {
    issues.push({
      parameter: "url",
      message: `Must not exceed ${String(SCREENSHOT_LIMITS.urlMaxLength)} characters.`
    });
  }
  const rawFormat = params.get("format");
  const format = rawFormat === null ? "png" : parseEnum(rawFormat, "format", SCREENSHOT_FORMATS, issues);
  const rawWaitUntil = params.get("wait_until");
  const waitUntil = rawWaitUntil === null ? DEFAULT_WAIT_UNTIL : parseEnum(rawWaitUntil, "wait_until", WAIT_UNTIL_VALUES, issues);
  const rawViewportWidth = params.get("viewport_width");
  const viewportWidth2 = rawViewportWidth === null ? SCREENSHOT_LIMITS.viewportWidth.default : parseInteger(rawViewportWidth, "viewport_width", SCREENSHOT_LIMITS.viewportWidth, issues);
  const rawViewportHeight = params.get("viewport_height");
  const viewportHeight2 = rawViewportHeight === null ? SCREENSHOT_LIMITS.viewportHeight.default : parseInteger(
    rawViewportHeight,
    "viewport_height",
    SCREENSHOT_LIMITS.viewportHeight,
    issues
  );
  const rawScale = params.get("device_scale_factor");
  const deviceScaleFactor2 = rawScale === null ? SCREENSHOT_LIMITS.deviceScaleFactor.default : parseDecimal(rawScale, "device_scale_factor", SCREENSHOT_LIMITS.deviceScaleFactor, issues);
  const rawFullPage = params.get("full_page");
  const fullPage = rawFullPage === null ? false : parseBoolean(rawFullPage, "full_page", issues);
  const rawSelector = params.get("selector");
  const selector = rawSelector === null ? null : parseSelector(rawSelector, issues);
  if (selector !== null && fullPage === true) {
    issues.push({ parameter: "selector", message: "Cannot be combined with full_page." });
  }
  const rawDarkMode = params.get("dark_mode");
  const darkMode = rawDarkMode === null ? false : parseBoolean(rawDarkMode, "dark_mode", issues);
  const rawDelay = params.get("delay_ms");
  const delayMs2 = rawDelay === null ? SCREENSHOT_LIMITS.delayMs.default : parseInteger(rawDelay, "delay_ms", SCREENSHOT_LIMITS.delayMs, issues);
  const rawTimeout = params.get("timeout_ms");
  const timeoutMs2 = rawTimeout === null ? SCREENSHOT_LIMITS.timeoutMs.default : parseInteger(rawTimeout, "timeout_ms", SCREENSHOT_LIMITS.timeoutMs, issues);
  const rawCacheTtl = params.get("cache_ttl");
  const cacheTtl = rawCacheTtl === null ? SCREENSHOT_LIMITS.cacheTtlSeconds.default : parseInteger(rawCacheTtl, "cache_ttl", SCREENSHOT_LIMITS.cacheTtlSeconds, issues);
  const rawQuality = params.get("quality");
  let quality2 = null;
  if (rawQuality !== null) {
    if (format === "png") {
      issues.push({
        parameter: "quality",
        message: "Only supported for the jpeg and webp formats."
      });
    } else {
      quality2 = parseInteger(rawQuality, "quality", SCREENSHOT_LIMITS.quality, issues);
    }
  } else if (format !== null && isLossyFormat(format)) {
    quality2 = SCREENSHOT_LIMITS.quality.lossyDefault;
  }
  const hideSelectors2 = parseHideSelectors(params.getAll("hide_selectors"), issues);
  const rawBlockCookieBanners = params.get("block_cookie_banners");
  const blockCookieBanners = rawBlockCookieBanners === null ? false : parseBoolean(rawBlockCookieBanners, "block_cookie_banners", issues);
  const rawCustomCss = params.get("custom_css");
  const customCss = rawCustomCss === null ? null : parseSource(rawCustomCss, "custom_css", SCREENSHOT_LIMITS.customCss.maxLength, issues);
  const rawCustomJs = params.get("custom_js");
  const customJs = rawCustomJs === null ? null : parseSource(rawCustomJs, "custom_js", SCREENSHOT_LIMITS.customJs.maxLength, issues);
  if (issues.length > 0) {
    issues.sort((a, b) => a.parameter < b.parameter ? -1 : a.parameter > b.parameter ? 1 : 0);
    return { ok: false, issues };
  }
  const candidate = {
    url: rawUrl,
    format,
    viewport_width: viewportWidth2,
    viewport_height: viewportHeight2,
    device_scale_factor: deviceScaleFactor2,
    full_page: fullPage,
    selector,
    quality: quality2,
    wait_until: waitUntil,
    delay_ms: delayMs2,
    timeout_ms: timeoutMs2,
    dark_mode: darkMode,
    hide_selectors: hideSelectors2,
    block_cookie_banners: blockCookieBanners,
    custom_css: customCss,
    custom_js: customJs,
    cache_ttl: cacheTtl
  };
  const parsed = normalizedScreenshotOptionsSchema.safeParse(candidate);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({
        parameter: issue.path.join(".") || "url",
        message: issue.message
      }))
    };
  }
  return { ok: true, options: parsed.data };
}
var SCREENSHOT_BODY_TYPES = {
  url: "string",
  format: "string",
  viewport_width: "integer",
  viewport_height: "integer",
  device_scale_factor: "number",
  full_page: "boolean",
  selector: "string",
  quality: "integer",
  wait_until: "string",
  delay_ms: "integer",
  timeout_ms: "integer",
  dark_mode: "boolean",
  hide_selectors: "array",
  block_cookie_banners: "boolean",
  custom_css: "string",
  custom_js: "string",
  cache_ttl: "integer"
};
function bodyValueToQuery(name, value) {
  switch (SCREENSHOT_BODY_TYPES[name]) {
    case "string":
      return typeof value === "string" ? { ok: true, values: [value] } : { ok: false, message: "Must be a JSON string." };
    case "integer":
    case "number":
      return typeof value === "number" ? { ok: true, values: [String(value)] } : { ok: false, message: "Must be a JSON number." };
    case "boolean":
      return typeof value === "boolean" ? { ok: true, values: [String(value)] } : { ok: false, message: "Must be a JSON boolean." };
    case "array":
      return Array.isArray(value) && value.every((item) => typeof item === "string") ? { ok: true, values: value } : { ok: false, message: "Must be an array of JSON strings." };
  }
}
function parseScreenshotBody(body) {
  const issues = [];
  const known = new Set(SCREENSHOT_PARAMETER_NAMES);
  const query = new URLSearchParams();
  const mistyped = /* @__PURE__ */ new Set();
  for (const [name, value] of Object.entries(body)) {
    if (!known.has(name)) {
      issues.push({ parameter: reportableName(name), message: "Unknown parameter." });
      continue;
    }
    if (value === null) continue;
    const parameter = name;
    const converted = bodyValueToQuery(parameter, value);
    if (!converted.ok) {
      issues.push({ parameter, message: converted.message });
      mistyped.add(parameter);
      continue;
    }
    for (const item of converted.values) query.append(parameter, item);
  }
  const parsed = parseScreenshotQuery(query);
  if (parsed.ok && issues.length === 0) return parsed;
  if (!parsed.ok) {
    issues.push(...parsed.issues.filter((issue) => !mistyped.has(issue.parameter)));
  }
  issues.sort((a, b) => a.parameter < b.parameter ? -1 : a.parameter > b.parameter ? 1 : 0);
  return { ok: false, issues };
}
function applyEntitlementBounds(options, entitlement) {
  return {
    ...options,
    timeout_ms: Math.min(options.timeout_ms, entitlement.maxTimeoutMs),
    delay_ms: Math.min(options.delay_ms, entitlement.maxDelayMs),
    cache_ttl: Math.min(options.cache_ttl, entitlement.maxCacheTtl)
  };
}

// src/screenshot/target.ts
function isForbiddenIpv4(host) {
  const parts = host.split(".");
  if (parts.length !== 4) return false;
  const octets = [];
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return false;
    const value = Number(part);
    if (value > 255) return false;
    octets.push(value);
  }
  const a = octets[0] ?? 0;
  const b = octets[1] ?? 0;
  const c = octets[2] ?? 0;
  if (a === 0) return true;
  if (a === 10) return true;
  if (a === 127) return true;
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a === 169 && b === 254) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 0 && c === 0) return true;
  if (a === 192 && b === 0 && c === 2) return true;
  if (a === 192 && b === 168) return true;
  if (a === 198 && (b === 18 || b === 19)) return true;
  if (a >= 224) return true;
  return false;
}
function parseIpv6(rawAddress) {
  let address = rawAddress.trim().toLowerCase();
  if (address.startsWith("[") && address.endsWith("]")) address = address.slice(1, -1);
  const zoneIndex = address.indexOf("%");
  if (zoneIndex !== -1) address = address.slice(0, zoneIndex);
  if (!address.includes(":")) return null;
  let tailBytes = [];
  const dotted = /:(\d{1,3}(?:\.\d{1,3}){3})$/.exec(address);
  if (dotted?.[1] !== void 0) {
    const octets = dotted[1].split(".").map(Number);
    if (octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) return null;
    tailBytes = octets;
    address = address.slice(0, address.length - dotted[1].length);
    if (address.endsWith(":") && !address.endsWith("::")) address = address.slice(0, -1);
  }
  const doubleColon = address.indexOf("::");
  const hasCompression = doubleColon !== -1;
  if (hasCompression && address.indexOf("::", doubleColon + 1) !== -1) return null;
  const [headText = "", tailText = ""] = hasCompression ? [address.slice(0, doubleColon), address.slice(doubleColon + 2)] : [address, ""];
  const toHextets = (text) => {
    if (text === "") return [];
    const parts = text.split(":");
    const values = [];
    for (const part of parts) {
      if (!/^[0-9a-f]{1,4}$/.test(part)) return null;
      values.push(Number.parseInt(part, 16));
    }
    return values;
  };
  const head = toHextets(headText);
  const tail = toHextets(tailText);
  if (head === null || tail === null) return null;
  const explicitBytes = (head.length + tail.length) * 2 + tailBytes.length;
  if (explicitBytes > 16) return null;
  if (!hasCompression && explicitBytes !== 16) return null;
  const bytes = [];
  for (const hextet of head) bytes.push(hextet >> 8, hextet & 255);
  for (let index = explicitBytes; index < 16; index += 1) bytes.push(0);
  for (const hextet of tail) bytes.push(hextet >> 8, hextet & 255);
  bytes.push(...tailBytes);
  return bytes.length === 16 ? bytes : null;
}
function isForbiddenIpv6(rawAddress) {
  const bytes = parseIpv6(rawAddress);
  if (bytes === null) return false;
  const [b0 = 0, b1 = 0] = bytes;
  if (b0 === 255) return true;
  if (b0 === 254 && (b1 & 192) === 128) return true;
  if ((b0 & 254) === 252) return true;
  const leadingZero = bytes.slice(0, 10).every((byte) => byte === 0);
  if (leadingZero) {
    const isMapped = bytes[10] === 255 && bytes[11] === 255;
    const isCompatible = bytes[10] === 0 && bytes[11] === 0;
    if (isMapped || isCompatible) {
      const embedded = bytes.slice(12).join(".");
      return isForbiddenIpv4(embedded);
    }
  }
  return false;
}
var FORBIDDEN_HOSTNAMES = /* @__PURE__ */ new Set([
  "localhost",
  "localhost.localdomain",
  "ip6-localhost",
  "ip6-loopback",
  "metadata",
  "metadata.google.internal",
  "instance-data"
]);
function isForbiddenHostname(hostname) {
  if (FORBIDDEN_HOSTNAMES.has(hostname)) return true;
  return hostname.endsWith(".localhost") || hostname.endsWith(".internal");
}
function canonicalize(url) {
  const canonical = new URL(url.href);
  canonical.hash = "";
  canonical.hostname = canonical.hostname.toLowerCase();
  if (canonical.protocol === "http:" && canonical.port === "80" || canonical.protocol === "https:" && canonical.port === "443") {
    canonical.port = "";
  }
  if (canonical.pathname === "") canonical.pathname = "/";
  return canonical.href.endsWith("?") ? canonical.href.slice(0, -1) : canonical.href;
}
function validateTargetUrl(rawUrl) {
  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return { ok: false, reason: "unparsable" };
  }
  if (!ALLOWED_TARGET_PROTOCOLS.includes(url.protocol)) {
    return { ok: false, reason: "forbidden_scheme" };
  }
  if (url.username !== "" || url.password !== "") {
    return { ok: false, reason: "embedded_credentials" };
  }
  if (url.hostname === "") {
    return { ok: false, reason: "missing_host" };
  }
  const hostname = url.hostname.toLowerCase();
  if (isForbiddenHostname(hostname) || isForbiddenIpv4(hostname) || isForbiddenIpv6(hostname)) {
    return { ok: false, reason: "forbidden_address" };
  }
  return { ok: true, url, canonical: canonicalize(url) };
}
function isForbiddenAddress(address) {
  const normalized = address.trim().toLowerCase();
  return normalized.includes(":") ? isForbiddenIpv6(normalized) : isForbiddenIpv4(normalized);
}

// src/screenshot/response.ts
var RESPONSE_HEADERS = {
  requestId: "X-Request-ID",
  cache: "X-Urlshot-Cache",
  creditsUsed: "X-Urlshot-Credits-Used",
  rateLimitLimit: "X-RateLimit-Limit",
  rateLimitRemaining: "X-RateLimit-Remaining",
  retryAfter: "Retry-After",
  /**
   * Which renderer produced the image, as `v<release>:<build>` — for example `v4:4572cafff9ea`.
   *
   * One header rather than two, and a short build rather than a full commit hash, because this is
   * read by us and seen by everyone. Two headers, one of them named for a function version,
   * describe the shape of the infrastructure twice over; a full forty-character hash pins the exact
   * source for anyone watching. Twelve characters identify a build unambiguously for the only
   * purpose this serves.
   *
   * That purpose: a release is a person moving an alias ([ADR 0021](../../../../docs/adr/0021-release-the-renderer-through-an-alias.md)),
   * so "is the new version actually serving?" has to be answerable from a response rather than by
   * correlating timestamps against a deploy log.
   *
   * The `v<release>:` prefix is absent when there is no qualifier — a local renderer, `$LATEST`, or
   * one deployed before this existed — leaving the build alone rather than an invented release.
   *
   * Carried on a cache hit too, describing the render that produced the image rather than the
   * request that found it, so a hit may name a release several deploys old. That is the honest
   * answer: those are the pixels it is serving.
   */
  renderer: "X-Urlshot-Renderer",
  /**
   * How long the caller's browser may reuse the image — see {@link imageCacheControl}.
   *
   * Without it, every page view re-fetched every embedded image: a response with neither this nor
   * `Last-Modified` is not reused by a browser, so a signed URL in an `<img>` cost a request, a
   * usage event and a database transaction per view, even for a visitor who saw it a minute before.
   */
  cacheControl: "Cache-Control"
};
function imageCacheControl(maxAgeSeconds) {
  const seconds = Math.floor(maxAgeSeconds);
  return seconds > 0 ? `private, max-age=${String(seconds)}` : "no-store";
}
var BUILD_ID_LENGTH = 12;
function formatRendererIdentity(rendererVersion, functionVersion) {
  const build = rendererVersion.slice(0, BUILD_ID_LENGTH);
  const release = functionVersion === void 0 || functionVersion === null || functionVersion === "$LATEST" ? null : functionVersion;
  return release === null ? build : `v${release}:${build}`;
}
function buildResponseHeaders(metadata) {
  const headers = {
    [RESPONSE_HEADERS.requestId]: metadata.requestId,
    [RESPONSE_HEADERS.cache]: metadata.cacheStatus,
    [RESPONSE_HEADERS.creditsUsed]: String(metadata.creditsUsed),
    [RESPONSE_HEADERS.rateLimitLimit]: String(metadata.rateLimitLimit)
  };
  if (metadata.rateLimitRemaining !== void 0) {
    headers[RESPONSE_HEADERS.rateLimitRemaining] = String(metadata.rateLimitRemaining);
  }
  if (metadata.retryAfterSeconds !== void 0) {
    headers[RESPONSE_HEADERS.retryAfter] = String(metadata.retryAfterSeconds);
  }
  if (metadata.renderer !== void 0 && metadata.renderer !== "") {
    headers[RESPONSE_HEADERS.renderer] = metadata.renderer;
  }
  if (metadata.browserMaxAgeSeconds !== void 0) {
    headers[RESPONSE_HEADERS.cacheControl] = imageCacheControl(metadata.browserMaxAgeSeconds);
  }
  return headers;
}
function contentTypeFor(format) {
  return FORMAT_CONTENT_TYPE[format];
}

// src/screenshot/cache-key.ts
function buildCacheKeyMaterial(input) {
  const parts = [
    `v=${API_VERSION}`,
    `ws=${encodeURIComponent(input.workspaceId)}`,
    `r=${encodeURIComponent(input.rendererVersion)}`
  ];
  for (const name of CONTENT_AFFECTING_OPTIONS) {
    const value = input.options[name];
    parts.push(`${name}=${encodeOptionValue(value)}`);
  }
  return parts.join("&");
}
function encodeOptionValue(value) {
  if (value === null) return "null";
  if (Array.isArray(value)) {
    return value.map((entry) => encodeURIComponent(entry)).join(",");
  }
  return encodeURIComponent(String(value));
}
async function computeCacheKeyDigest(material) {
  const bytes = new TextEncoder().encode(material);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
function buildCacheKeyUrl(digest) {
  return `https://cache.urlshot.internal/${API_VERSION}/screenshot/${digest}`;
}
var RENDER_METADATA_HEADER = "X-Urlshot-Render";
var RENDER_REQUEST_ID_HEADER = "X-Request-ID";
var RENDERER_FUNCTION_TIMEOUT_MS = 6e4;
var renderRequestSchema = z.object({
  /**
   * Carried for log correlation and cost attribution only. The renderer never uses it for a policy
   * decision, a limit, or a cache key, and it is the only tenant-identifying value the renderer
   * sees. Optional so a manual or diagnostic call does not have to invent one.
   */
  workspaceId: z.string().min(1).optional(),
  options: normalizedScreenshotOptionsSchema
});
var renderTimingsSchema = z.object({
  /** Wall-clock time inside the renderer, including browser acquisition. */
  totalMs: z.int().min(0),
  /** Time spent acquiring or launching a browser. Distinguishes a cold start from slow pages. */
  browserMs: z.int().min(0).nullable(),
  navigationMs: z.int().min(0).nullable(),
  screenshotMs: z.int().min(0).nullable()
});
var renderMetadataSchema = z.object({
  requestId: z.string().min(1),
  /**
   * Whether Chromium work began for this request.
   *
   * `true` means the attempt is billable even if it later failed. `false` means every failure was
   * a pre-browser failure and the reservation must be released without charge.
   */
  browserStarted: z.boolean(),
  /** Renderer compatibility version, which also participates in the cache key. */
  rendererVersion: z.string().min(1),
  /**
   * The Lambda version that served this request, as Lambda itself reports it.
   *
   * `$LATEST`, or a number when invoked through a qualifier — which is every customer request,
   * since the gateway calls the `live` alias ([ADR 0021](../../../../docs/adr/0021-release-the-renderer-through-an-alias.md)).
   * With releases done by moving that alias by hand, this is the only thing that answers "which
   * version actually served this?" without correlating timestamps against a deploy log.
   *
   * Optional rather than required, and deliberately: the gateway parses this schema against
   * whatever renderer is deployed, and a renderer that predates this field would otherwise fail to
   * parse entirely. Absent means "an older renderer", not "unknown version".
   */
  functionVersion: z.string().min(1).nullish(),
  /** Whether this invocation paid a cold start, for cost and latency analysis. */
  cold: z.boolean(),
  /** Hostname only. The full target URL never leaves the renderer in metadata. */
  targetHostname: z.string().nullable(),
  /** HTTP status returned by the target page, when navigation got far enough to see one. */
  targetStatus: z.int().min(100).max(599).nullable(),
  redirectCount: z.int().min(0),
  contentType: z.string().nullable(),
  byteLength: z.int().min(0).nullable(),
  timings: renderTimingsSchema
});
var RENDERER_ERROR_CODES = [
  "invalid_request",
  "target_not_allowed",
  "navigation_failed",
  "render_timeout",
  "render_failed",
  "script_failed",
  "selector_not_found"
];
var RENDERER_ERROR_STATUS = 422;
var renderErrorBodySchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string().min(1),
    requestId: z.string().min(1)
  })
});
var NON_ASCII = /[^\x20-\x7E]/g;
function encodeRenderMetadata(metadata) {
  return JSON.stringify(metadata).replace(
    NON_ASCII,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
}
function decodeRenderMetadata(headerValue) {
  if (headerValue == null || headerValue === "") return null;
  try {
    const parsed = JSON.parse(headerValue);
    const result = renderMetadataSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}
var CACHE_STATUSES = ["HIT", "MISS", "BYPASS"];
var cacheStatusSchema = z.enum(CACHE_STATUSES);
var CACHE_SOURCES = ["edge", "renderer", "bypass"];
var cacheSourceSchema = z.enum(CACHE_SOURCES);
var CREDIT_DISPOSITIONS = [
  "render_attempt_charged",
  "cache_hit",
  "pre_browser_failure"
];
var creditDispositionSchema = z.enum(CREDIT_DISPOSITIONS);
var AUTHENTICATION_MODES = ["api_key", "signed_url", "demo"];
var authenticationModeSchema = z.enum(AUTHENTICATION_MODES);
var usageRenderDetailSchema = z.object({
  rendererVersion: z.string().min(1),
  /**
   * The Lambda version that served the render, as Lambda reports it.
   *
   * Stored beside the build because they answer different questions. The build says what code ran
   * and stays resolvable in git forever; the release says which deployment served it, and a release
   * is a person moving an alias ([ADR 0021](../../../../docs/adr/0021-release-the-renderer-through-an-alias.md)).
   * Without this a logged render cannot say whether it ran before or after a flip -- the question
   * the `X-Urlshot-Renderer` header answers for a live request and nothing answered for a past one.
   *
   * Nullable rather than required: a consumer validates this against whatever gateway is deployed,
   * a renderer that predates the field reports nothing, and a render that never started has no
   * answer to give.
   */
  functionVersion: z.string().min(1).nullish(),
  cold: z.boolean(),
  browserStarted: z.boolean(),
  targetStatus: z.int().min(100).max(599).nullable(),
  redirectCount: z.int().min(0),
  timings: renderTimingsSchema
});
var usageEventSchema = z.object({
  /** Idempotency key for ingestion. Stable for one request outcome across queue redeliveries. */
  eventId: z.string().min(1).max(128),
  requestId: z.string().min(1).max(128),
  workspaceId: z.string().min(1).max(64),
  /** Null when the request was rejected before a key could be identified. */
  keyId: z.string().min(1).max(64).nullable(),
  authenticationMode: authenticationModeSchema,
  status: requestStatusSchema,
  /** Null on success; otherwise the public error code the customer received. */
  errorCode: errorCodeSchema.nullable(),
  httpStatus: z.int().min(100).max(599),
  creditDisposition: creditDispositionSchema,
  /** 0 or 1 in the MVP, where one attempt that reaches Chromium costs exactly one credit. */
  credits: z.int().min(0).max(1),
  cacheStatus: cacheStatusSchema,
  cacheSource: cacheSourceSchema,
  /** Total gateway-observed duration, including cache lookup and renderer time. */
  durationMs: z.int().min(0),
  /** Hostname only, never the path or query string. */
  targetHostname: z.string().max(255).nullable(),
  /**
   * Normalized options **without the target URL**, or null when the request never got past
   * validation.
   *
   * The URL is omitted rather than merely unused. A full URL is not a non-secret value: signed
   * links, session identifiers and access tokens live in query strings, and a screenshot API is
   * exactly where a customer passes one. `RenderRequest.targetHostname` stores the hostname alone
   * and says so, which is pointless if the whole URL travels beside it in this object.
   *
   * It was included, so every request log held the full URL while the dashboard told the customer
   * "The target URL is not stored". Omitting it here rather than stripping it on receipt means it
   * never enters the queue either -- Cloudflare retains queue messages for days.
   */
  options: recordedScreenshotOptionsSchema.nullable(),
  render: usageRenderDetailSchema.nullable(),
  occurredAt: z.iso.datetime()
});
var USAGE_INGESTION_PATH = "/api/internal/usage-events";
var USAGE_BATCH_MAX_EVENTS = 100;
var usageEventBatchSchema = z.object({
  events: z.array(usageEventSchema).min(1).max(USAGE_BATCH_MAX_EVENTS)
});
var usageIngestionResultSchema = z.object({
  accepted: z.int().min(0),
  duplicates: z.int().min(0),
  rejected: z.array(z.object({ eventId: z.string(), reason: z.string() }))
});
function usageEventIdFor(requestId) {
  return `usage_${requestId}`;
}
var PUBLIC_PLAN_CODES = ["free", "startup", "growth", "scale"];
var INTERNAL_PLAN_CODES = ["demo"];
var PLAN_CODES = [...PUBLIC_PLAN_CODES, ...INTERNAL_PLAN_CODES];
var planCodeSchema = z.enum(PLAN_CODES);
function isInternalPlanCode(code) {
  return INTERNAL_PLAN_CODES.includes(code);
}
var planLimitsSchema = z.object({
  /** Render credits per billing period. One true cache-miss attempt costs one credit. */
  monthlyCredits: z.int().min(0),
  /**
   * Threshold for the gateway's coarse abuse shield. **Not a published entitlement.**
   *
   * `coarse-rate-limit.ts` counts in the isolate's memory, so this number is per-location and
   * per-isolate: a client spread across Cloudflare locations gets more than it says, and an isolate
   * eviction resets the window. That module states the rule directly -- nothing there may ever
   * become the basis of a billing or entitlement decision -- and this field is its input.
   *
   * It is also the wrong shape to publish. A render takes one to thirty seconds, so a per-second
   * rate bounds no resource: three requests a second against ten-second renders reaches thirty
   * simultaneous renders without ever tripping. {@link PlanLimits.concurrentRenders} is what
   * actually bounds the renderer, and it is the number the pricing table and the API reference
   * show.
   *
   * Kept comfortably above `concurrentRenders` on every plan, and asserted so. Set at or below it,
   * this approximate per-location counter would refuse a burst *before* the exact one did -- so a
   * customer inside their concurrency allowance would see `rate_limit_exceeded` instead of
   * `concurrency_limit_exceeded`, intermittently, depending which Cloudflare location they reached.
   *
   * Kept under this name rather than renamed: it is a Prisma column in a shipped migration, and a
   * rename would be a database migration and a four-service coordinated deploy to improve a word.
   */
  requestsPerSecond: z.int().min(1),
  /**
   * Renders that may run at once. The published concurrency entitlement.
   *
   * Counted exactly, in the workspace's Durable Object, in the same transaction that reserves the
   * credit -- so this is the number that bounds live Chromium processes and the renderer bill.
   */
  concurrentRenders: z.int().min(1),
  maxTimeoutMs: z.int().min(1e3),
  maxDelayMs: z.int().min(0),
  maxCacheTtl: z.int().min(0),
  /**
   * Whether `custom_js` may be used. The published capability entitlement.
   *
   * A named entitlement rather than a plan comparison, as every limit here is: the gateway asks
   * `limits.customJs`, never `planCode === 'free'`. That is what lets a plan's capabilities change
   * in one seeded table instead of in enforcement code.
   *
   * False on Free because arbitrary JavaScript on a free key is a general-purpose headless browser
   * anyone can rent for nothing, and false on the website demo for the same reason with none of the
   * accountability. `custom_css` is not gated: a stylesheet cannot read a page or make a request.
   */
  customJs: z.boolean()
});
var billingPeriodSchema = z.object({
  /** Stable identifier for the period, used to reset counters exactly once. */
  id: z.string().min(1).max(64),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime()
});
var entitlementSnapshotObject = z.object({
  workspaceId: z.string().min(1).max(64),
  planCode: planCodeSchema,
  /** Monotonically increasing. A lower version must never overwrite a higher one. */
  entitlementVersion: z.int().min(1),
  billingPeriod: billingPeriodSchema,
  limits: planLimitsSchema
});
function fillSeededCapabilities(value) {
  if (typeof value !== "object" || value === null) return value;
  const snapshot = value;
  const { planCode, limits } = snapshot;
  if (typeof limits !== "object" || limits === null) return value;
  if (limits.customJs !== void 0) return value;
  if (typeof planCode !== "string" || !PLAN_CODES.includes(planCode)) {
    return value;
  }
  return {
    ...snapshot,
    limits: { ...limits, customJs: SEED_PLAN_LIMITS[planCode].customJs }
  };
}
var entitlementSnapshotSchema = z.preprocess(
  fillSeededCapabilities,
  entitlementSnapshotObject
);
var KEY_STATUSES = ["active", "revoked"];
var keyStatusSchema = z.enum(KEY_STATUSES);
var edgeKeySnapshotSchema = z.object({
  keyId: z.string().min(1).max(64),
  workspaceId: z.string().min(1).max(64),
  /**
   * Whether this key may only authenticate a signed request.
   *
   * A signed URL carries the key itself, so a key intended for embedding must be useless as a
   * bearer token -- otherwise publishing the URL publishes a credential. Fixed when the key is
   * created and never changed: loosening it would arm every URL already embedded in every page.
   *
   * Defaulted rather than required so a snapshot written by an older control plane still parses as
   * the safe, ordinary case rather than failing the lookup and locking a workspace out.
   */
  kind: keyKindSchema.default("secret")
});
var SEED_PLAN_LIMITS = {
  /**
   * Matches the free tier the market treats as standard: 100 a month, no card.
   *
   * The ceilings are lower than the paid plans deliberately -- a free workspace that could hold a
   * render open for thirty seconds is the cheapest way to burn Chromium time for nothing.
   */
  free: {
    monthlyCredits: 100,
    requestsPerSecond: 5,
    concurrentRenders: 1,
    maxTimeoutMs: 15e3,
    maxDelayMs: 3e3,
    maxCacheTtl: 3600,
    customJs: false
  },
  /**
   * Entry paid tier: a round thousand, at a price that does not gate anyone out.
   *
   * The ladder is 1,000 / 10,000 / 100,000 because that is the shape buyers compare against and
   * because round numbers survive being quoted from memory. The entry price is deliberately not the
   * cheapest available -- undercutting at the bottom attracts the customers least likely to stay,
   * and the tiers above are where the argument is made.
   */
  startup: {
    monthlyCredits: 1e3,
    requestsPerSecond: 20,
    concurrentRenders: 5,
    maxTimeoutMs: 3e4,
    maxDelayMs: 1e4,
    maxCacheTtl: 86400,
    customJs: true
  },
  /**
   * The tier most customers who outgrow Startup will land on.
   *
   * It differs from Startup only in volume and concurrency, and that is not an oversight: the
   * public contract caps `timeout_ms` at 30s, `delay_ms` at 10s and `cache_ttl` at 24h, and Startup
   * already sits on all three ceilings. A plan cannot offer more than the API accepts.
   */
  growth: {
    monthlyCredits: 1e4,
    requestsPerSecond: 60,
    concurrentRenders: 20,
    maxTimeoutMs: 3e4,
    maxDelayMs: 1e4,
    maxCacheTtl: 86400,
    customJs: true
  },
  /**
   * Volume, and enough concurrency for a burst to complete rather than be refused.
   *
   * Fifty is a product choice, not a capacity ceiling: the renderer's Lambda has no reserved
   * concurrency and the account quota is a thousand, so this leaves twenty times headroom. It is
   * set where it is because a page embedding many captures fires them all at once, and a plan at
   * this volume is bought by someone doing exactly that.
   */
  scale: {
    monthlyCredits: 1e5,
    requestsPerSecond: 150,
    concurrentRenders: 50,
    maxTimeoutMs: 3e4,
    maxDelayMs: 1e4,
    maxCacheTtl: 86400,
    customJs: true
  },
  /**
   * The website demo's workspace. Not sold, not listed.
   *
   * The ceilings match exactly what the demo endpoint requests -- a ten-second timeout, no delay, a
   * day of cache -- so the gateway never clamps a demo request into something other than what the
   * visitor is shown. Credits are a monthly backstop above the demo's own daily budget: if the
   * per-visitor and per-day counters ever fail open, this is where the spend stops.
   *
   * Two renders at a time, so one slow page cannot starve every other visitor, and no more, because
   * the demo is a taste of the product rather than a free tier.
   */
  demo: {
    monthlyCredits: 5e3,
    requestsPerSecond: 10,
    concurrentRenders: 2,
    maxTimeoutMs: 1e4,
    maxDelayMs: 0,
    maxCacheTtl: 86400,
    customJs: false
  }
};
var PLAN_DISPLAY_NAMES = {
  free: "Free",
  startup: "Startup",
  growth: "Growth",
  scale: "Scale",
  demo: "Website demo"
};
var INTERNAL_SIGNATURE_HEADER = "X-Urlshot-Internal-Signature";
var INTERNAL_TIMESTAMP_HEADER = "X-Urlshot-Internal-Timestamp";
var INTERNAL_SIGNATURE_MAX_SKEW_MS = 5 * 60 * 1e3;
var INTERNAL_SIGNATURE_VERSION = "v1";
function internalTimestamp(at = /* @__PURE__ */ new Date()) {
  return String(at.getTime());
}
function parseInternalTimestamp(value) {
  if (!/^[0-9]{1,15}$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
function internalSigningPayload(input) {
  return [
    INTERNAL_SIGNATURE_VERSION,
    input.timestamp,
    input.method.toUpperCase(),
    input.path,
    input.body
  ].join("\n");
}
var edgeSyncEntitlementSchema = z.object({
  workspaceId: z.string().min(1).max(64),
  planCode: planCodeSchema,
  entitlementVersion: z.int().min(1),
  billingPeriod: billingPeriodSchema,
  limits: planLimitsSchema
});
var edgeSyncKeySchema = z.object({
  keyId: z.string().min(1).max(64),
  workspaceId: z.string().min(1).max(64),
  keyDigest: z.string().min(32).max(128),
  keyStatus: keyStatusSchema,
  /** Safe display prefix, for support correlation only. Never the full key. */
  keyPrefix: z.string().min(1).max(32),
  /** See `edgeKeySnapshotSchema.kind`. Defaulted for the same reason. */
  kind: keyKindSchema.default("secret")
});
var edgeSyncRetirementSchema = z.object({
  workspaceId: z.string().min(1).max(64),
  /** Every key digest the workspace has ever had, including already-revoked keys. */
  keyDigests: z.array(z.string().min(32).max(128)).max(500).default([])
});
var edgeSyncRequestSchema = z.object({
  issuedAt: z.iso.datetime(),
  entitlements: z.array(edgeSyncEntitlementSchema).max(100).default([]),
  keys: z.array(edgeSyncKeySchema).max(100).default([]),
  /**
   * Keys to reject immediately.
   *
   * Sent in addition to a `revoked` entry in `keys` because the Durable Object holds the revocation
   * override that lets the gateway reject a key whose KV snapshot is still stale.
   *
   * Each entry carries its workspace. Exact state lives in one Durable Object *per workspace*
   * (ADR 0004), so a bare key identifier cannot be routed anywhere -- an earlier version of this
   * field was a plain string array, and revocation silently did nothing because the gateway had no
   * way to find the object holding the override.
   */
  revokedKeyIds: z.array(z.object({ keyId: z.string().min(1).max(64), workspaceId: z.string().min(1).max(64) })).max(100).default([]),
  /**
   * Workspaces to erase from the edge entirely.
   *
   * Distinct from revoking every key. Revocation leaves the workspace's Durable Object holding its
   * counters and overrides; retirement destroys the object's storage, which is the only moment that
   * can happen -- see {@link edgeSyncRetirementSchema}.
   *
   * Capped low because each entry is a destructive operation against a separate Durable Object, and
   * a batch of them is a slow request rather than a large payload.
   */
  retiredWorkspaces: z.array(edgeSyncRetirementSchema).max(20).default([])
});
var edgeSyncResultSchema = z.object({
  /** Per-workspace outcome, so the Platform API can retry only what did not apply. */
  entitlements: z.array(
    z.object({
      workspaceId: z.string(),
      applied: z.boolean(),
      /** The version now held by the Durable Object, which may be newer than the one sent. */
      entitlementVersion: z.int().min(0)
    })
  ),
  keysApplied: z.int().min(0),
  keysRevoked: z.int().min(0),
  /** Workspaces whose edge state was destroyed, so the caller knows it is safe to delete its own. */
  workspacesRetired: z.int().min(0).default(0)
});

// src/internal/signature.ts
function toHex2(buffer) {
  return Array.from(new Uint8Array(buffer)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
async function hmac2(secret, payload) {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return toHex2(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(payload)));
}
function timingSafeEqualHex(a, b) {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let index = 0; index < a.length; index += 1) {
    difference |= a.charCodeAt(index) ^ b.charCodeAt(index);
  }
  return difference === 0;
}
async function signInternalRequest(input) {
  const timestamp = internalTimestamp(input.at ?? /* @__PURE__ */ new Date());
  const signature = await hmac2(
    input.secret,
    internalSigningPayload({
      timestamp,
      method: input.method,
      path: input.path,
      body: input.body
    })
  );
  return { signature, timestamp };
}
async function verifyInternalSignature(input) {
  if (input.signature === null || input.signature === "" || input.timestamp === null || input.timestamp === "") {
    return { ok: false, reason: "missing_headers" };
  }
  const issuedAt = parseInternalTimestamp(input.timestamp);
  if (issuedAt === null) return { ok: false, reason: "malformed_timestamp" };
  const now = input.now ?? Date.now();
  if (Math.abs(now - issuedAt) > INTERNAL_SIGNATURE_MAX_SKEW_MS) {
    return { ok: false, reason: "stale_timestamp" };
  }
  const expected = await hmac2(
    input.secret,
    internalSigningPayload({
      timestamp: input.timestamp,
      method: input.method,
      path: input.path,
      body: input.body
    })
  );
  return timingSafeEqualHex(input.signature, expected) ? { ok: true } : { ok: false, reason: "bad_signature" };
}
function stableFragment(value) {
  if (Array.isArray(value)) return value.map(stableFragment);
  if (value === null || typeof value !== "object") return value;
  const source = value;
  const sorted = {};
  for (const key of Object.keys(source).sort()) {
    sorted[key] = stableFragment(source[key]);
  }
  return sorted;
}
function fragmentFor(schema) {
  const generated = z.toJSONSchema(schema, { io: "input" });
  const { $schema: _ignored, ...rest } = generated;
  return stableFragment(rest);
}
function buildParameters() {
  const parameters = [];
  for (const name of SCREENSHOT_PARAMETER_NAMES) {
    if (name === "hide_selectors") {
      const itemFragment = fragmentFor(screenshotFieldSchemas.hide_selectors);
      const { description: description2 = "", ...itemSchema } = itemFragment;
      parameters.push({
        name,
        in: "query",
        required: false,
        description: String(description2),
        // Repeated as `?hide_selectors=.a&hide_selectors=.b`, which is `form`/`explode`.
        style: "form",
        explode: true,
        schema: stableFragment({
          type: "array",
          items: itemSchema,
          maxItems: SCREENSHOT_LIMITS.hideSelectors.maxCount
        })
      });
      continue;
    }
    const fragment = fragmentFor(screenshotFieldSchemas[name]);
    const { description = "", ...schema } = fragment;
    parameters.push({
      name,
      in: "query",
      required: name === "url",
      description: String(description),
      schema
    });
  }
  return parameters;
}
function buildRequestBodySchema() {
  const properties = {};
  for (const name of SCREENSHOT_PARAMETER_NAMES) {
    if (name === "hide_selectors") {
      const { description: _repeatable, ...itemSchema } = fragmentFor(
        screenshotFieldSchemas.hide_selectors
      );
      properties[name] = stableFragment({
        type: "array",
        items: itemSchema,
        maxItems: SCREENSHOT_LIMITS.hideSelectors.maxCount,
        description: "CSS selectors whose matching elements are hidden after navigation."
      });
      continue;
    }
    properties[name] = fragmentFor(screenshotFieldSchemas[name]);
  }
  return {
    type: "object",
    description: [
      "The options of `GET /v1/screenshot`, as a JSON object. Every property has the name, bounds",
      "and default of the query parameter it replaces, and values take their JSON type: `true`,",
      'not `"true"`. A property set to `null` is treated as absent, and an unknown property is an',
      "error, as an unknown query parameter is."
    ].join("\n"),
    required: ["url"],
    additionalProperties: false,
    properties
  };
}
function errorCodesByStatus() {
  const grouped = /* @__PURE__ */ new Map();
  for (const code of ERROR_CODES) {
    const status = ERROR_HTTP_STATUS[code];
    const existing = grouped.get(status);
    if (existing) {
      existing.push(code);
    } else {
      grouped.set(status, [code]);
    }
  }
  return new Map([...grouped.entries()].sort((a, b) => a[0] - b[0]));
}
var STATUS_SUMMARY = {
  400: "The request could not be validated, the target address is not permitted, or the page did not have what the request asked for: custom_js failed, or no element matched selector.",
  401: "The API key is missing, invalid, or revoked.",
  429: "A rate, concurrency, or monthly credit limit was reached.",
  502: "The target page could not be loaded or captured.",
  503: "The screenshot service is temporarily unavailable.",
  504: "The render exceeded the effective timeout."
};
function buildResponseHeaderDefinitions() {
  return {
    [RESPONSE_HEADERS.requestId]: {
      description: "Opaque identifier for this request. Quote it in any support conversation.",
      schema: { type: "string" }
    },
    [RESPONSE_HEADERS.cache]: {
      description: "HIT when the image was served from cache, MISS when it was rendered, BYPASS when cache_ttl was 0.",
      schema: { type: "string", enum: ["HIT", "MISS", "BYPASS"] }
    },
    [RESPONSE_HEADERS.creditsUsed]: {
      description: "Render credits consumed by this request. A cache hit consumes zero.",
      schema: { type: "integer", minimum: 0 }
    },
    [RESPONSE_HEADERS.rateLimitLimit]: {
      description: "Renders your plan may run at once.",
      schema: { type: "integer", minimum: 0 }
    },
    [RESPONSE_HEADERS.renderer]: {
      description: "The renderer build that produced the image, such as v4:4572cafff9ea; on a cache hit, the build that made the cached image. For support conversations, not for branching on.",
      schema: { type: "string" }
    },
    [RESPONSE_HEADERS.rateLimitRemaining]: {
      description: "Render slots still free after this request. Absent on a cache hit, where it is unknown rather than zero.",
      schema: { type: "integer", minimum: 0 }
    },
    [RESPONSE_HEADERS.cacheControl]: {
      description: "How long a browser may reuse the image: private, max-age=<seconds> when cache_ttl is set, and on a cache hit no longer than the cached copy has left; no-store when cache_ttl is 0.",
      schema: { type: "string" }
    }
  };
}
function headerRefs(names) {
  const refs = {};
  for (const name of names) {
    refs[name] = { $ref: `#/components/headers/${name}` };
  }
  return refs;
}
function buildOpenApiDocument() {
  const parameters = buildParameters();
  const grouped = errorCodesByStatus();
  const errorResponses = {};
  for (const [status, codes] of grouped) {
    const codeList = codes.map((code) => `\`${code}\``).join(", ");
    const retryable = codes.filter((code) => RETRYABLE_ERROR_CODES.includes(code));
    const responseHeaders = headerRefs([
      RESPONSE_HEADERS.requestId,
      RESPONSE_HEADERS.cache,
      RESPONSE_HEADERS.creditsUsed
    ]);
    if (retryable.length > 0) {
      responseHeaders[RESPONSE_HEADERS.retryAfter] = {
        description: "Seconds to wait before retrying.",
        schema: { type: "integer", minimum: 0 }
      };
    }
    errorResponses[String(status)] = {
      description: `${STATUS_SUMMARY[status] ?? "Request failed."} Codes: ${codeList}.`,
      headers: responseHeaders,
      content: {
        "application/json": {
          schema: { $ref: "#/components/schemas/Error" }
        }
      }
    };
  }
  const imageContent = {};
  for (const format of SCREENSHOT_FORMATS) {
    imageContent[FORMAT_CONTENT_TYPE[format]] = {
      schema: { type: "string", format: "binary" }
    };
  }
  const successResponse = {
    description: "The captured screenshot.",
    headers: headerRefs([
      RESPONSE_HEADERS.requestId,
      RESPONSE_HEADERS.cache,
      RESPONSE_HEADERS.creditsUsed,
      RESPONSE_HEADERS.rateLimitLimit,
      RESPONSE_HEADERS.rateLimitRemaining,
      RESPONSE_HEADERS.renderer,
      RESPONSE_HEADERS.cacheControl
    ]),
    content: imageContent
  };
  const bodyOnlyResponses = {
    "413": {
      description: `The request body is larger than ${String(SCREENSHOT_BODY_MAX_BYTES)} bytes. Code: \`invalid_request\`.`,
      headers: headerRefs([
        RESPONSE_HEADERS.requestId,
        RESPONSE_HEADERS.cache,
        RESPONSE_HEADERS.creditsUsed
      ]),
      content: { "application/json": { schema: { $ref: "#/components/schemas/Error" } } }
    },
    "415": {
      description: "The request body is not sent as `Content-Type: application/json`. Code: `invalid_request`.",
      headers: headerRefs([
        RESPONSE_HEADERS.requestId,
        RESPONSE_HEADERS.cache,
        RESPONSE_HEADERS.creditsUsed
      ]),
      content: { "application/json": { schema: { $ref: "#/components/schemas/Error" } } }
    }
  };
  const postErrorResponses = Object.fromEntries(
    Object.entries({ ...errorResponses, ...bodyOnlyResponses }).sort(
      ([a], [b]) => Number(a) - Number(b)
    )
  );
  return {
    openapi: "3.1.0",
    info: {
      title: "urlshot.io Screenshot API",
      version: CONTRACT_VERSION,
      summary: "Capture a screenshot of any public web page over HTTP.",
      description: [
        "Authenticate with a secret key using `Authorization: Bearer " + SECRET_KEY_PREFIX + "...`.",
        "",
        "Alternatively, sign the query string and pass a publishable key (`" + PUBLISHABLE_KEY_PREFIX + "...`) as the",
        "`" + SIGNED_KEY_PARAMETER + "` parameter, which needs",
        "no header and is therefore safe to embed in an `<img>` tag. See the `" + SIGNATURE_PARAMETER + "` security",
        "scheme for the payload construction. Signed URLs are `GET` only.",
        "",
        "Options travel as query parameters on `GET`, or as a JSON object in the body of a `POST`.",
        "The two are equivalent -- the same names, validation, price and cache -- and `POST` suits",
        "`custom_css` and `custom_js` better: source code is an ordinary JSON string rather than",
        "percent-encoded, it stays out of the URL that logs and proxies record, and text outside",
        "ASCII cannot push the request past the 64 KB URL limit of the edge in front of the API.",
        "",
        "A successful request returns the image bytes directly. Any request that cannot return an",
        "image returns the JSON error envelope described by the `Error` schema.",
        "",
        "Caching is opt in. `cache_ttl=0`, the default, always renders fresh. A cache hit consumes",
        "zero render credits; a render attempt that reaches the browser consumes exactly one,",
        "even if the target page then fails to load."
      ].join("\n")
    },
    servers: [{ url: "https://api.urlshot.io", description: "Production" }],
    /**
     * Two ways to authenticate, as alternatives.
     *
     * The signed-URL requirement names both query parameters in one object, because a `key`
     * without a `signature` is not a credential -- an OpenAPI requirement object is an AND, and
     * the array of them is the OR.
     */
    security: [{ bearerAuth: [] }, { signedUrlKey: [], signedUrlSignature: [] }],
    tags: [{ name: "Screenshots", description: "Synchronous screenshot capture." }],
    paths: {
      [SCREENSHOT_PATH]: {
        get: {
          operationId: "getScreenshot",
          tags: ["Screenshots"],
          summary: "Capture a screenshot",
          /**
           * The rendering environment is described here because its limits are silent.
           *
           * A page using WebGL does not fail: it draws whatever fallback it has, or nothing, and
           * returns 200. A page asking for an uninstalled font does not fail either; it
           * substitutes. Both produce a screenshot that differs from the customer's own browser,
           * with no error to explain why -- which arrives as a support request rather than a bug
           * report.
           */
          description: [
            "Renders the target URL and returns the encoded image. Options that affect the produced image also affect the cache key.",
            "",
            "The same request can be sent as `POST` with a JSON body, which is the better choice when it carries `custom_css` or `custom_js`.",
            "",
            "### Rendering environment",
            "",
            "Pages are rendered by headless Chromium on a machine with no GPU.",
            "",
            "- **WebGL and GPU-accelerated canvas are unavailable.** A page depending on them renders its fallback, or an empty area, and still returns `200`. Charting and mapping libraries that fall back to 2D canvas or SVG capture correctly; those requiring WebGL do not.",
            "- **Only these font families are installed:** Liberation (metric-compatible with Arial, Times New Roman and Courier New), Noto Sans, Noto CJK and Noto Color Emoji. Any other family is substituted, so text may wrap and lay out differently from your own browser. Web fonts the page loads over the network are used normally.",
            "",
            "None of these produce an error, so a screenshot that looks wrong rather than missing is usually one of them."
          ].join("\n"),
          parameters,
          responses: {
            "200": successResponse,
            ...errorResponses
          }
        },
        post: {
          operationId: "postScreenshot",
          tags: ["Screenshots"],
          summary: "Capture a screenshot, with options in a JSON body",
          description: [
            "The `GET` operation with its options in a JSON body instead of the query string. The response, the price and the cache are the same: a `GET` and a `POST` with the same options share one cache entry.",
            "",
            "Send `Content-Type: application/json` and no query parameters. The body may be at most " + String(SCREENSHOT_BODY_MAX_BYTES) + " bytes, which admits every option at its limit however the JSON is escaped.",
            "",
            "Bearer keys only: a signed URL signs its query string, and a body is not part of it.",
            "",
            "The rendering environment is described under the `GET` operation."
          ].join("\n"),
          security: [{ bearerAuth: [] }],
          requestBody: {
            required: true,
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ScreenshotRequest" }
              }
            }
          },
          responses: {
            "200": successResponse,
            ...postErrorResponses
          }
        }
      }
    },
    components: {
      securitySchemes: {
        bearerAuth: {
          type: "http",
          scheme: "bearer",
          description: "A secret key (`" + SECRET_KEY_PREFIX + "...`), shown once at creation and never retrievable again. A publishable key is refused here: it belongs in a signed URL, not in a header."
        },
        signedUrlKey: {
          type: "apiKey",
          in: "query",
          name: SIGNED_KEY_PARAMETER,
          description: "A publishable key (`" + PUBLISHABLE_KEY_PREFIX + "...`), carried in the query so the URL needs no header. Included in the signed payload, so a signature cannot be replayed against another key. A secret key is refused here."
        },
        signedUrlSignature: {
          type: "apiKey",
          in: "query",
          name: SIGNATURE_PARAMETER,
          description: [
            "Hex HMAC-SHA-256 over the request's own query string, keyed by the signing secret",
            "issued with the API key.",
            "",
            "The signed payload is the query string exactly as it will be sent, with the",
            "`" + SIGNATURE_PARAMETER + "` pair removed. Nothing is prepended, and parameters are **not** sorted or",
            "normalised: sign the exact bytes you are about to send."
          ].join("\n")
        }
      },
      headers: buildResponseHeaderDefinitions(),
      schemas: {
        ScreenshotRequest: buildRequestBodySchema(),
        ErrorCode: {
          type: "string",
          description: "Stable machine-readable error identifier.",
          enum: [...ERROR_CODES]
        },
        Error: {
          type: "object",
          required: ["error"],
          properties: {
            error: {
              type: "object",
              required: ["code", "message", "requestId"],
              properties: {
                code: { $ref: "#/components/schemas/ErrorCode" },
                message: {
                  type: "string",
                  description: "Human-readable explanation. Never contains a value you supplied. A rejected parameter is named, and an unrecognised name is repeated back only when it is a short identifier, so the message is always safe to log and to display."
                },
                requestId: {
                  type: "string",
                  description: "Matches the X-Request-ID response header."
                }
              }
            }
          },
          examples: [
            {
              error: {
                code: "monthly_limit_exceeded",
                message: ERROR_MESSAGES.monthly_limit_exceeded,
                requestId: "req_0123456789abcdef0123456789abcdef"
              }
            }
          ]
        }
      }
    }
  };
}
function serializeOpenApiDocument(document) {
  return `${JSON.stringify(document, null, 2)}
`;
}
var OPENAPI_OUTPUT_PATH = `openapi/urlshot-${API_VERSION}.json`;

// src/openapi/diff.ts
function asObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value : null;
}
function asArray(value) {
  return Array.isArray(value) ? value : null;
}
function asNumber(value) {
  return typeof value === "number" ? value : null;
}
function indexParameters(operation) {
  const result = /* @__PURE__ */ new Map();
  for (const entry of asArray(operation.parameters) ?? []) {
    const parameter = asObject(entry);
    if (!parameter || typeof parameter.name !== "string") continue;
    result.set(parameter.name, {
      name: parameter.name,
      required: parameter.required === true,
      schema: asObject(parameter.schema) ?? {}
    });
  }
  return result;
}
function compareEnums(location, previous, next, changes) {
  const previousEnum = asArray(previous.enum);
  if (!previousEnum) return;
  const nextEnum = new Set(asArray(next.enum) ?? []);
  const removed = previousEnum.filter((value) => !nextEnum.has(value));
  if (removed.length > 0) {
    changes.push({
      location,
      message: `Enum values removed: ${removed.map((value) => String(value)).join(", ")}.`
    });
  }
}
function compareBounds(location, previous, next, changes) {
  const tightened = [
    ["minimum", "raised"],
    ["minLength", "raised"],
    ["minItems", "raised"],
    ["maximum", "lowered"],
    ["maxLength", "lowered"],
    ["maxItems", "lowered"]
  ];
  for (const [key, direction] of tightened) {
    const before = asNumber(previous[key]);
    const after = asNumber(next[key]);
    if (before === null) {
      if (after !== null) {
        changes.push({ location, message: `Bound "${key}" introduced (${String(after)}).` });
      }
      continue;
    }
    if (after === null) continue;
    const isTighter = direction === "raised" ? after > before : after < before;
    if (isTighter) {
      changes.push({
        location,
        message: `Bound "${key}" ${direction} from ${String(before)} to ${String(after)}.`
      });
    }
  }
}
function compareSchema(location, previous, next, changes) {
  if (typeof previous.type === "string" && previous.type !== next.type) {
    const after = typeof next.type === "string" ? next.type : "absent";
    changes.push({
      location,
      message: `Type changed from "${previous.type}" to "${after}".`
    });
  }
  compareEnums(location, previous, next, changes);
  compareBounds(location, previous, next, changes);
  const previousItems = asObject(previous.items);
  const nextItems = asObject(next.items);
  if (previousItems && nextItems) {
    compareSchema(`${location}.items`, previousItems, nextItems, changes);
  }
}
function compareOperation(location, previous, next, changes) {
  const previousParameters = indexParameters(previous);
  const nextParameters = indexParameters(next);
  for (const [name, previousParameter] of previousParameters) {
    const nextParameter = nextParameters.get(name);
    if (!nextParameter) {
      changes.push({ location: `${location}.parameters.${name}`, message: "Parameter removed." });
      continue;
    }
    if (!previousParameter.required && nextParameter.required) {
      changes.push({
        location: `${location}.parameters.${name}`,
        message: "Parameter became required."
      });
    }
    compareSchema(
      `${location}.parameters.${name}.schema`,
      previousParameter.schema,
      nextParameter.schema,
      changes
    );
  }
  for (const [name, nextParameter] of nextParameters) {
    if (!previousParameters.has(name) && nextParameter.required) {
      changes.push({
        location: `${location}.parameters.${name}`,
        message: "New required parameter added."
      });
    }
  }
  const previousResponses = asObject(previous.responses) ?? {};
  const nextResponses = asObject(next.responses) ?? {};
  for (const status of Object.keys(previousResponses)) {
    const nextResponse = asObject(nextResponses[status]);
    if (!nextResponse) {
      changes.push({ location: `${location}.responses.${status}`, message: "Response removed." });
      continue;
    }
    const previousContent = asObject(asObject(previousResponses[status])?.content);
    const nextContent = asObject(nextResponse.content);
    if (!previousContent) continue;
    for (const mediaType of Object.keys(previousContent)) {
      if (!nextContent || !(mediaType in nextContent)) {
        changes.push({
          location: `${location}.responses.${status}.content.${mediaType}`,
          message: "Response media type removed."
        });
      }
    }
  }
}
var HTTP_METHODS = ["get", "put", "post", "delete", "options", "head", "patch", "trace"];
function detectBreakingChanges(previousDocument, nextDocument) {
  const changes = [];
  const previous = asObject(previousDocument);
  const next = asObject(nextDocument);
  if (!previous || !next) {
    return [{ location: "(root)", message: "One of the documents is not an OpenAPI object." }];
  }
  const previousPaths = asObject(previous.paths) ?? {};
  const nextPaths = asObject(next.paths) ?? {};
  for (const path of Object.keys(previousPaths)) {
    const previousItem = asObject(previousPaths[path]);
    const nextItem = asObject(nextPaths[path]);
    if (!previousItem) continue;
    if (!nextItem) {
      changes.push({ location: `paths.${path}`, message: "Path removed." });
      continue;
    }
    for (const method of HTTP_METHODS) {
      const previousOperation = asObject(previousItem[method]);
      if (!previousOperation) continue;
      const nextOperation = asObject(nextItem[method]);
      if (!nextOperation) {
        changes.push({ location: `paths.${path}.${method}`, message: "Operation removed." });
        continue;
      }
      compareOperation(`paths.${path}.${method}`, previousOperation, nextOperation, changes);
    }
  }
  const previousSchemas = asObject(asObject(previous.components)?.schemas) ?? {};
  const nextSchemas = asObject(asObject(next.components)?.schemas) ?? {};
  for (const name of Object.keys(previousSchemas)) {
    const previousSchema = asObject(previousSchemas[name]);
    const nextSchema = asObject(nextSchemas[name]);
    if (!previousSchema) continue;
    if (!nextSchema) {
      changes.push({ location: `components.schemas.${name}`, message: "Schema removed." });
      continue;
    }
    compareSchema(`components.schemas.${name}`, previousSchema, nextSchema, changes);
  }
  const previousSecurity = asObject(asObject(previous.components)?.securitySchemes) ?? {};
  const nextSecurity = asObject(asObject(next.components)?.securitySchemes) ?? {};
  for (const name of Object.keys(previousSecurity)) {
    if (!(name in nextSecurity)) {
      changes.push({
        location: `components.securitySchemes.${name}`,
        message: "Security scheme removed."
      });
    }
  }
  return changes;
}
function formatBreakingChanges(changes) {
  return changes.map((change) => `  - ${change.location}: ${change.message}`).join("\n");
}
function parseVersion(value) {
  if (typeof value !== "string") return null;
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(value);
  if (match === null) return null;
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}
function isNewer(next, previous) {
  for (let index = 0; index < 3; index += 1) {
    const a = next[index] ?? 0;
    const b = previous[index] ?? 0;
    if (a !== b) return a > b;
  }
  return false;
}
function contentOf(document) {
  const info = asObject(document.info) ?? {};
  const { version: _version, ...rest } = info;
  return JSON.stringify({ ...document, info: rest });
}
function checkContractVersion(previousDocument, nextDocument) {
  const previous = asObject(previousDocument);
  const next = asObject(nextDocument);
  if (!previous || !next) return "One of the documents is not an OpenAPI object.";
  if (contentOf(previous) === contentOf(next)) return null;
  const previousVersion = parseVersion(asObject(previous.info)?.version);
  const nextVersion = parseVersion(asObject(next.info)?.version);
  if (nextVersion === null) return "info.version is not a major.minor.patch version.";
  if (previousVersion === null || isNewer(nextVersion, previousVersion)) return null;
  return `The OpenAPI document changed but info.version is ${nextVersion.join(".")}, not higher than ${previousVersion.join(".")}. Raise CONTRACT_VERSION in packages/api-contract/src/version.ts (patch for wording, minor for an addition), then run pnpm openapi:generate.`;
}

export { ALLOWED_TARGET_PROTOCOLS, API_VERSION, AUTHENTICATION_MODES, AUTO_WAIT, CACHE_SOURCES, CACHE_STATUSES, CONTENT_AFFECTING_OPTIONS, CONTRACT_VERSION, CREDIT_DISPOSITIONS, DEFAULT_WAIT_UNTIL, DEMO_BODY_MAX_BYTES, DEMO_ERROR_CODES, DEMO_ERROR_HTTP_STATUS, DEMO_ERROR_MESSAGES, DEMO_LIMITS, DEMO_PATH, DEMO_RENDER, DEMO_VIEWPORTS, DEMO_WORKSPACE_ID, ERROR_CODES, ERROR_HTTP_STATUS, ERROR_MESSAGES, ERROR_STATUS, FORMAT_CONTENT_TYPE, INTERNAL_PLAN_CODES, INTERNAL_SIGNATURE_HEADER, INTERNAL_SIGNATURE_MAX_SKEW_MS, INTERNAL_SIGNATURE_VERSION, INTERNAL_TIMESTAMP_HEADER, KEY_KINDS, KEY_STATUSES, LOSSY_FORMATS, OPENAPI_OUTPUT_PATH, PLAN_CODES, PLAN_DISPLAY_NAMES, PUBLIC_PLAN_CODES, PUBLISHABLE_KEY_PREFIX, RENDERER_ERROR_CODES, RENDERER_ERROR_STATUS, RENDERER_FUNCTION_TIMEOUT_MS, RENDER_METADATA_HEADER, RENDER_REQUEST_ID_HEADER, REQUEST_STATUSES, RESPONSE_HEADERS, RETRYABLE_ERROR_CODES, SCREENSHOT_BODY_MAX_BYTES, SCREENSHOT_BODY_TYPES, SCREENSHOT_FORMATS, SCREENSHOT_LIMITS, SCREENSHOT_PARAMETER_NAMES, SCREENSHOT_PATH, SECRET_KEY_PREFIX, SEED_PLAN_LIMITS, SIGNATURE_PARAMETER, SIGNED_KEY_PARAMETER, USAGE_BATCH_MAX_EVENTS, USAGE_INGESTION_PATH, WAIT_UNTIL_VALUES, applyEntitlementBounds, authenticationModeSchema, billingPeriodSchema, buildCacheKeyMaterial, buildCacheKeyUrl, buildOpenApiDocument, buildResponseHeaders, buildSignedQueryPayload, cacheSourceSchema, cacheStatusSchema, checkContractVersion, computeCacheKeyDigest, contentTypeFor, creditDispositionSchema, decodeRenderMetadata, demoErrorBody, demoErrorBodySchema, demoRequestSchema, demoScreenshotQuery, describeValidationIssues, detectBreakingChanges, edgeKeySnapshotSchema, edgeSyncEntitlementSchema, edgeSyncKeySchema, edgeSyncRequestSchema, edgeSyncResultSchema, edgeSyncRetirementSchema, encodeRenderMetadata, entitlementSnapshotSchema, errorBody, errorBodySchema, errorCodeSchema, formatBreakingChanges, formatRendererIdentity, imageCacheControl, internalSigningPayload, internalTimestamp, isForbiddenAddress, isInternalPlanCode, isLossyFormat, isPreBrowserErrorCode, isValidSelector, keyKindSchema, keyPrefixFor, keyStatusSchema, normalizeSelectors, normalizedScreenshotOptionsSchema, parseInternalTimestamp, parseScreenshotBody, parseScreenshotQuery, planCodeSchema, planLimitsSchema, readSignature, recordedScreenshotOptionsSchema, renderErrorBodySchema, renderMetadataSchema, renderRequestSchema, renderTimingsSchema, requestStatusSchema, screenshotFieldSchemas, serializeOpenApiDocument, signInternalRequest, signScreenshotQuery, timingSafeEqualHex, timingSafeEqualSignature, usageEventBatchSchema, usageEventIdFor, usageEventSchema, usageIngestionResultSchema, usageRenderDetailSchema, validateTargetUrl, verifyInternalSignature, verifyScreenshotQuery };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map